import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from 'lodash';
import {
  useDataFeedWizardStore,
  useInstrumentItems,
} from '../../../../store/dataFeedWizard';
import {
  IllioSearchOption,
  MappingItem,
} from '../../../../types/generatedTypes';
import Actions from '../../../../store/Actions';
import { useTableSearch } from '../../../../hooks/useTableSearch';
import { useDisabledRows } from '../../hooks/useDisabledRows';
import { useInstrumentPrice } from '../../hooks/useInstrumentPrice';
import { coerceValue } from '../../utils/valueCoercion';

export const useLinkInstrumentsStep = () => {
  const {
    setInstrumentMappings,
    setOTCMappings,
    updateItemMapping,
    setCurrentStepButtonState,
    registerStepActions,
    clearStepActions,
    sessionId,
    setStepLoading,
    setPristineState,
    isMoving,
    setIsMoving,
  } = useDataFeedWizardStore();

  const [isSaving, setIsSaving] = useState(false);
  const [searchLoadingRows, setSearchLoadingRows] = useState<
    Record<string, boolean>
  >({});
  const [searchOptions, setSearchOptions] = useState<
    Record<string, IllioSearchOption[]>
  >({});

  // Get instrument items using stable selector hook (prevents infinite re-renders)
  const instrumentItems = useInstrumentItems();

  const { isItemDisabledAccountOnly } = useDisabledRows();
  const { prices, fetchPrice, fetchPricesForItems, clearPrice } = useInstrumentPrice();

  // Load instrument matches on mount - always fetch fresh from backend
  useEffect(() => {
    const loadInstruments = async () => {
      if (!sessionId) {
        console.warn('Missing sessionId - cannot load instruments');
        return;
      }

      setStepLoading(true);
      try {
        const response = await Actions.matchWizardInstruments(sessionId);

        // Convert API response to store format (no category field needed)
        const instruments: MappingItem[] = response.instruments.map(inst => ({
          id: inst.id,
          isin: inst.isin,
          symbol: inst.symbol,
          instrument_source_id: inst.instrument_source_id,
          name: inst.name,
          code: inst.code,
          currency_code: inst.currency_code,
          label: inst.label,
          multiplier: inst.multiplier,
          original_multiplier: inst.original_multiplier,
          instrument_type: inst.instrument_type,
          status: inst.status,
          options: inst.options,
          account_numbers: inst.account_numbers,
        }));

        // Set instruments directly
        setInstrumentMappings(instruments);
        // Set pristine to freshly loaded data
        setPristineState('step3', instruments);
        // Fetch prices for pre-matched instruments
        fetchPricesForItems(instruments);
      } catch (error) {
        console.error('Failed to load instruments:', error);
        setInstrumentMappings([]);
      } finally {
        setStepLoading(false);
      }
    };

    loadInstruments();
  }, [sessionId]);

  // Track initial sort to preserve row position after selection
  const initialSortDone = useRef(false);
  const sortedIndices = useRef<Record<string, number>>({});

  // Transform mappings to DataGrid rows format
  // Using selector, only instrument items are returned
  const rows = useMemo(() => {
    const mappedRows = instrumentItems.map(mapping => ({
      id: mapping.id,
      isin: mapping.isin,
      symbol: mapping.symbol,
      instrument_source_id: mapping.instrument_source_id,
      name: mapping.name,
      code: mapping.code,
      currency_code: mapping.currency_code,
      label: mapping.label,
      multiplier: mapping.multiplier,
      original_multiplier: mapping.original_multiplier,
      instrument_type: mapping.instrument_type,
      status: mapping.status,
      options: mapping.options,
      account_numbers: mapping.account_numbers,
    }));

    // On first load: sort with disabled-account items at bottom, pending before matched within each group
    if (!initialSortDone.current && mappedRows.length > 0) {
      mappedRows.sort((a, b) => {
        const aDisabled = isItemDisabledAccountOnly(a.account_numbers) ? 1 : 0;
        const bDisabled = isItemDisabledAccountOnly(b.account_numbers) ? 1 : 0;
        if (aDisabled !== bDisabled) return aDisabled - bDisabled;
        if (a.status === 'pending' && b.status === 'matched') return -1;
        if (a.status === 'matched' && b.status === 'pending') return 1;
        return 0;
      });
      mappedRows.forEach((row, index) => {
        sortedIndices.current[row.id] = index;
      });
      initialSortDone.current = true;
      return mappedRows;
    }

    // After initial sort: preserve order using stored indices
    return mappedRows.sort((a, b) => {
      const indexA = sortedIndices.current[a.id] ?? Number.MAX_SAFE_INTEGER;
      const indexB = sortedIndices.current[b.id] ?? Number.MAX_SAFE_INTEGER;
      return indexA - indexB;
    });
  }, [instrumentItems, isItemDisabledAccountOnly]);

  const { searchTerm: tableSearchTerm, handleSearch: handleTableSearch, filteredData: filteredRows } = useTableSearch(rows, { debounceMs: 300 });

  // Bank-specific option detection helpers
  // Note: These patterns are specific to how each bank formats option symbols
  // in their vault files, which then flow through to our instrument linking step

  /**
   * Check if a symbol represents an option using Poalim bank's naming convention.
   *
   * Poalim uses symbols that end with:
   * - '+p' or '+P' for Put options
   * - '+c' or '+C' for Call options
   *
   * Examples:
   * - "AAPL+P" → true (Put option)
   * - "TSLA+C" → true (Call option)
   * - "MSFT" → false (Regular stock)
   *
   * @param symbol - Symbol string to check (case-insensitive)
   * @returns true if symbol matches Poalim's option pattern
   *
   * @note This pattern is specific to Poalim (בנק הפועלים).
   *       Other banks may use different option identification patterns.
   */
  const isPoalimOptionSymbol = (symbol: string | null | undefined): boolean => {
    if (!symbol) return false;
    const symbolLower = symbol.toLowerCase();
    return symbolLower.endsWith('+p') || symbolLower.endsWith('+c');
  };

  // Debounced search function for live Illio search
  const handleSearch = useMemo(
    () =>
      debounce(async (rowId: string, query: string) => {
        if (query.length < 2) {
          setSearchOptions(prev => {
            const next = { ...prev };
            delete next[rowId];
            return next;
          });
          return;
        }

        setSearchLoadingRows(prev => ({ ...prev, [rowId]: true }));
        try {
          // Look up the row to check its symbol field
          const row = instrumentItems.find(m => m.id === rowId);
          const symbol = row?.symbol;

          // Use Poalim-specific option detection to determine search type
          // This helps Illio search return appropriate option instruments
          const isOption = isPoalimOptionSymbol(symbol);

          const type = isOption ? 'option' : undefined;
          const response = await Actions.searchWizardInstruments(query, type);
          const options = response.options || [];
          setSearchOptions(prev => ({
            ...prev,
            [rowId]: options.map((opt: IllioSearchOption) => ({
              id: opt.id,
              name: opt.name,
              code: opt.code,
              currency_code: opt.currency_code,
              label: opt.label,
              source_data: opt.source_data,
              original_multiplier: opt.original_multiplier ?? null,
              instrument_type: opt.instrument_type ?? null,
            })),
          }));
        } catch (error) {
          console.error('Illio search failed:', error);
          setSearchOptions(prev => ({ ...prev, [rowId]: [] }));
        } finally {
          setSearchLoadingRows(prev => ({ ...prev, [rowId]: false }));
        }
      }, 500),
    [instrumentItems]
  );

  // Cleanup debounced function on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      handleSearch.cancel();
    };
  }, [handleSearch]);

  const getAvailableOptionsForRow = (rowId: string): IllioSearchOption[] => {
    const mapping = instrumentItems.find(m => m.id === rowId);
    const allOptions: IllioSearchOption[] = [];

    // Always include currently selected instrument so MUI Autocomplete can display it
    if (
      mapping?.instrument_source_id?.source_id &&
      mapping.name &&
      mapping.label &&
      mapping.currency_code
    ) {
      allOptions.push({
        id: mapping.instrument_source_id.source_id,
        name: mapping.name,
        currency_code: mapping.currency_code,
        label: mapping.label,
        source_data: mapping.instrument_source_id.source_data || '',
        original_multiplier: mapping.original_multiplier ?? null,
        instrument_type: mapping.instrument_type ?? null,
        code: mapping.code,
      });
    }

    const isSearching = searchOptions[rowId] !== undefined;

    if (isSearching) {
      // User is actively searching — show only search results (+ selected)
      searchOptions[rowId].forEach(opt => {
        if (!allOptions.find(o => o.id === opt.id)) {
          allOptions.push(opt);
        }
      });
    } else {
      // Not searching — show pre-loaded options from backend matching
      const existingOptions =
        mapping?.options?.map(opt => ({
          id: opt.id,
          name: opt.name,
          currency_code: opt.currency_code,
          label: opt.label,
          source_data: opt.source_data,
          original_multiplier: opt.original_multiplier ?? null,
          instrument_type: opt.instrument_type ?? null,
          code: opt.code,
        })) || [];

      existingOptions.forEach(opt => {
        if (!allOptions.find(o => o.id === opt.id)) {
          allOptions.push(opt);
        }
      });
    }

    return allOptions;
  };

  const handleIllioInstrumentChange = (
    rowId: string,
    instrumentSourceId: {
      source_id: string | null;
      source_data: string | null;
    } | null,
    name: string | null,
    code: string | null,
    currencyCode: string | null,
    label: string | null,
    originalMultiplier: number | null,
    instrumentType: string | null
  ) => {
    let multiplierToUse = originalMultiplier;

    // Check if this mapping already exists in other rows - inherit their multiplier
    if (instrumentSourceId?.source_id) {
      const existingRow = instrumentItems.find(
        item =>
          item.id !== rowId &&
          item.instrument_source_id?.source_id === instrumentSourceId.source_id
      );
      if (existingRow?.multiplier != null) {
        multiplierToUse = existingRow.multiplier;
      }
    }

    updateItemMapping(rowId, {
      instrument_source_id: instrumentSourceId,
      name,
      code,
      currency_code: currencyCode,
      label,
      multiplier: multiplierToUse,
      instrument_type: instrumentType,
      original_multiplier: originalMultiplier,
    });

    // Fetch or clear price based on selection
    if (instrumentSourceId?.source_id && instrumentSourceId?.source_data) {
      fetchPrice(rowId, instrumentSourceId.source_id, instrumentSourceId.source_data);
    } else {
      clearPrice(rowId);
    }
  };

  const handleRowUpdate = useCallback(
    (newRow: MappingItem, oldRow: MappingItem) => {
      if (newRow.multiplier !== oldRow.multiplier) {
        const coercedMultiplier = coerceValue(newRow.multiplier) as number | null;
        const sourceId = newRow.instrument_source_id?.source_id;
        if (sourceId) {
          // Update multiplier for all rows with the same linked instrument
          instrumentItems.forEach(item => {
            if (item.instrument_source_id?.source_id === sourceId) {
              updateItemMapping(item.id, { multiplier: coercedMultiplier });
            }
          });
        } else {
          updateItemMapping(newRow.id, { multiplier: coercedMultiplier });
        }
      }
      return newRow;
    },
    [updateItemMapping, instrumentItems]
  );

  // Memoize validateFields to prevent infinite loops in action registration useEffect
  // Always allow proceeding - validation message shown but doesn't block navigation
  // Backend handles partial mappings gracefully (unmapped items are filtered out)
  const validateFields = useCallback((): boolean => {
    return true;
  }, []);

  // Save instrument mappings to backend
  const handleNext = useCallback(async (): Promise<boolean | number> => {
    // Allow proceeding with unmapped items - backend handles partial mappings gracefully

    if (!sessionId) {
      console.error('Missing sessionId - cannot save instrument mappings');
      return false;
    }

    setIsSaving(true);
    try {
      // Convert store format back to API format - using snake_case field names
      const mappingsToSave = instrumentItems.map(m => ({
        id: m.id,
        isin: m.isin,
        symbol: m.symbol,
        instrument_source_id: m.instrument_source_id,
        name: m.name,
        code: m.code,
        currency_code: m.currency_code,
        label: m.label,
        multiplier: m.multiplier,
        original_multiplier: m.original_multiplier,
        instrument_type: m.instrument_type,
        status: m.status,
      }));

      await Actions.saveWizardInstrumentMappings(sessionId, mappingsToSave);

      // Update pristine state after successful save
      setPristineState('step3', instrumentItems);

      return true;
    } catch (error) {
      console.error('Failed to save instrument mappings:', error);
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [sessionId, instrumentItems, setPristineState]);

  // Update button state whenever dependencies change
  // Update button state whenever dependencies change
  // Show informational message about unmapped items but don't block navigation
  useEffect(() => {
    const unmappedCount = instrumentItems.filter(
      m => !m.instrument_source_id?.source_id
    ).length;

    setCurrentStepButtonState({
      disabled: isMoving,
      loading: isSaving || isMoving,
      validationMessage:
        unmappedCount > 0
          ? `${unmappedCount} instrument${unmappedCount !== 1 ? 's' : ''} remaining to map.`
          : undefined,
    });
  }, [instrumentItems, isSaving, isMoving]);

  // Register step actions when component mounts
  // Note: registerStepActions and clearStepActions are omitted from deps - Zustand store functions are stable
  useEffect(() => {
    registerStepActions({
      onNext: handleNext,
      validateFields,
    });

    return () => {
      clearStepActions();
    };
  }, [handleNext, validateFields]);

  // Handler to move an instrument to OTC step - preserves unsaved selections
  const handleMoveToOTC = useCallback(
    async (instrumentId: string) => {
      if (isMoving) return;

      const item = instrumentItems.find(m => m.id === instrumentId);
      if (!item) return;

      if (!sessionId) {
        console.error('Missing sessionId - cannot move item');
        return;
      }

      setIsMoving(true);
      try {
        // Capture current state before API call (preserves unsaved selections)
        const currentInstruments = instrumentItems;

        // Call backend API
        const response = await Actions.moveWizardItem(
          sessionId,
          instrumentId,
          'otc'
        );
        const backendData = response as unknown as {
          instrument_mappings: MappingItem[];
          otc_mappings: MappingItem[];
        };

        // Merge: Keep current unsaved selections, just remove the moved item
        const updatedInstruments = currentInstruments.filter(
          m => m.id !== instrumentId
        );
        setInstrumentMappings(updatedInstruments);

        // OTC: Use backend response (includes the newly moved item with reset fields)
        setOTCMappings(backendData.otc_mappings);

        // Update pristine state for both affected steps
        setPristineState('step3', updatedInstruments);
        setPristineState('step4', backendData.otc_mappings);
      } catch (error) {
        console.error('Failed to move item to OTC:', error);
      } finally {
        setIsMoving(false);
      }
    },
    [
      isMoving,
      instrumentItems,
      sessionId,
      setInstrumentMappings,
      setOTCMappings,
      setPristineState,
      setIsMoving,
    ]
  );

  return {
    rows: filteredRows,
    getAvailableOptionsForRow,
    handleIllioInstrumentChange,
    handleSearch,
    searchLoadingRows,
    handleMoveToOTC,
    handleRowUpdate,
    tableSearchTerm,
    handleTableSearch,
    isItemDisabledAccountOnly,
    prices,
  };
};
